import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounceCallback } from 'usehooks-ts';

import { UI_ACTION_DEBOUNCE_MS } from '@/shared/utils';

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  Button,
  useSidebar,
  SheetFooter,
  Flex,
  cn,
  EmptyState,
  Text,
} from '@kognitos/lattice';
import { useElementDimensions } from '@/shared/hooks';
import {
  useAutomationInputsStore,
  selectClearValuesAndDefaults,
} from '@/shared/stores/automation-inputs.store';
import {
  ManagedTourProvider,
  TourTrigger,
  inputsTriggersTour,
  ETourId,
  useTourStore,
  selectActiveTourId,
  isTourCompleted,
} from '@/shared/tours';

import type { Commonv1Value } from '@/generated/hooks/grimoire/models';
import type { AutomationInputsSheetProps } from './types';
import { AutomationInputs } from './AutomationInputs';
import { useDefaultInputsHistory } from './hooks/use-default-inputs-history';
import { TriggerTypeSelector } from '@/modules/triggers';

/**
 * Single-line input types that should submit the inputs form when the user
 * presses Enter. Multi-line controls (textarea for complex types), selects,
 * checkboxes, file pickers and buttons are intentionally excluded so Enter
 * keeps its native behavior there.
 */
const ENTER_SUBMIT_INPUT_TYPES = new Set([
  'text',
  'number',
  'password',
  'email',
  'search',
  'tel',
  'url',
]);

/**
 * Bottom sheet component for managing automation inputs
 *
 * Displays a floating bottom sheet with a glassmorphic design containing:
 * - Input field collection based on specifications
 * - Responsive positioning relative to editor container or sidebar
 * - Save and Save & Run actions
 *
 * The sheet automatically positions itself to align with the editor container
 * width when available, or falls back to centered positioning based on sidebar state.
 *
 * Note: This component does not include the trigger button. The trigger should be
 * managed by the parent component using InputsTriggerButton.
 *
 * @param props - Component props
 * @returns JSX element containing the sheet content
 */
export const AutomationInputsSheet = React.memo(
  ({
    isOpen,
    onOpenChange,
    inputSpecs,
    values,
    errors,
    onValuesChange,
    onErrorsChange,
    onSaveInputs,
    onRun,
    editorContainerRef,
    placement = 'bottom',
    isRunMode = false,
    isTestMode = false,
    isReadonly = false,
    automationId,
    automationName,
    isDraft = true,
    invocationDetails,
    defaultInputKeys,
    onToggleDefault,
  }: AutomationInputsSheetProps) => {
    const { t } = useTranslation('automation-inputs');
    const { t: tCommon } = useTranslation('common');
    const { state } = useSidebar();
    const clearValuesAndDefaults = useAutomationInputsStore(
      selectClearValuesAndDefaults,
    );
    const {
      inputHistoryMap: _inputHistoryMap,
      fileHistoryMap,
      isLoading: _isHistoryLoading,
      error: _historyError,
    } = useDefaultInputsHistory(automationId, inputSpecs);

    /**
     * Handles selecting a historical file version as the current input value.
     *
     * @param inputName - The name of the input field
     * @param fileValue - The historical file Commonv1Value to set as current
     */
    const handleSelectHistoryFile = (
      inputName: string,
      fileValue: Commonv1Value,
    ) => {
      const newValues = { ...values, [inputName]: fileValue };
      onValuesChange?.(newValues);
    };

    // Calculate offset based on sidebar state
    // Expanded sidebar: 16rem (256px), Collapsed: 3rem (48px)
    const sidebarWidth = state === 'collapsed' ? '3rem' : '16rem';

    // Track editor container dimensions
    const { width: containerWidth, left: containerLeft } = useElementDimensions(
      editorContainerRef || { current: null },
    );

    const activeTourId = useTourStore(selectActiveTourId);

    // Checked upfront (not reactively) so the Sheet opens as non-modal from
    // the first render when the tour will run. Switching modal dynamically
    // after mount causes the Sheet to re-animate (open → close → reopen).
    const isTourPending =
      isDraft &&
      !isRunMode &&
      !isTestMode &&
      !isTourCompleted(ETourId.INPUTS_TRIGGERS);

    /**
     * Handles sheet open state changes.
     * Blocks closing while the inputs & triggers tour is active
     * to prevent the tour overlay clicks from dismissing the sheet.
     */
    const handleOpenChange = (open: boolean) => {
      if (!open && activeTourId === ETourId.INPUTS_TRIGGERS) {
        return;
      }
      if (!open) {
        // Clear errors when closing the sheet
        onErrorsChange?.({});
      }
      onOpenChange(open);
    };

    // Handle save & run / test — the sheet's `onRun` callback, shared by
    // both run mode ("Save & run") and test mode ("Test").
    //
    // The button is wrapped in a leading-edge debounce so a double-click —
    // common because the button label changes to a no-op state only after
    // the dialog detaches — fires onRun exactly once. Without this, two
    // near-simultaneous clicks both pass the dispatch (qa-agent#252 /
    // concurrent-run-clicks).
    const handleRunAction = useDebounceCallback(
      () => {
        // Let the run handler own validation, highlighting, and sheet closing.
        onRun?.(values);
      },
      UI_ACTION_DEBOUNCE_MS,
      { leading: true, trailing: false },
    );

    // Handle clear all
    const handleClearAll = () => {
      clearValuesAndDefaults(automationId);
    };

    // Check if there are values to clear
    const hasValues = Object.keys(values).length > 0;

    // Check if inputSpecs has any items
    const hasInputSpecs = inputSpecs && inputSpecs.length > 0;

    /**
     * Submits the inputs when the user presses Enter inside a single-line text
     * field, mirroring the footer's primary action (Save & Run in run mode,
     * Save otherwise). Without this, Enter is a no-op and users must click the
     * button explicitly (MAN-2547).
     *
     * @param event - The keyboard event from the inputs panel.
     */
    const handleInputsKeyDown = (
      event: React.KeyboardEvent<HTMLDivElement>,
    ) => {
      if (event.key !== 'Enter' || event.shiftKey) return;
      if (isReadonly || !hasInputSpecs) return;
      // Don't hijack Enter while an IME composition is in progress.
      if (event.nativeEvent.isComposing) return;

      const target = event.target as HTMLElement;
      if (target.tagName !== 'INPUT') return;
      if (!ENTER_SUBMIT_INPUT_TYPES.has((target as HTMLInputElement).type)) {
        return;
      }

      event.preventDefault();
      if (isRunMode || isTestMode) {
        handleRunAction();
      } else {
        onSaveInputs?.();
      }
    };

    return (
      <>
        <Sheet
          open={isOpen}
          onOpenChange={handleOpenChange}
          modal={!isTourPending}
        >
          <SheetContent
            side="bottom"
            className={cn(
              // Override default bottom sheet styles for custom floating design
              // Translate classes, not a transform: the slide animation owns `transform`.
              placement === 'center'
                ? 'top-1/2 bottom-auto max-h-[80vh] -translate-x-1/2 -translate-y-1/2'
                : 'bottom-3 max-h-[80vh]',
              // Figma design styles
              'rounded-xl border border-border',
              'bg-card',
              'backdrop-blur-3',
              // Shadow
              'shadow-xl',
              // Positioning
              placement === 'bottom' && !containerWidth && '-translate-x-1/2',
              'inset-x-auto',
            )}
            style={
              placement === 'center'
                ? {
                    // Centred in the space beside the sidebar, both ways.
                    left: `calc(${sidebarWidth} + (100% - ${sidebarWidth}) / 2)`,
                    width: `min(40rem, calc(100% - ${sidebarWidth} - 2rem))`,
                  }
                : containerWidth && containerLeft
                  ? {
                      left: `${containerLeft - 4}px`,
                      width: `${containerWidth + 8}px`,
                      transform: 'none',
                    }
                  : {
                      left: `calc(${sidebarWidth} + (100% - ${sidebarWidth}) / 2)`,
                      width: '70%',
                    }
            }
          >
            <div className="h-full flex flex-col gap-5 overflow-hidden">
              {/* Header */}
              <SheetHeader className="gap-2 p-5 border-b border-none pb-0">
                {isRunMode ? (
                  <>
                    <SheetTitle className="text-xl font-medium">
                      {t('sheet.runModeTitle')}
                    </SheetTitle>
                    <SheetDescription className="text-base">
                      {t('sheet.runModeDescription')}
                    </SheetDescription>
                  </>
                ) : isTestMode ? (
                  <>
                    <SheetTitle className="text-xl font-medium">
                      {hasInputSpecs
                        ? t('sheet.testModeTitle')
                        : t('sheet.testModeNoInputsTitle')}
                    </SheetTitle>
                    <SheetDescription className="text-base">
                      {hasInputSpecs
                        ? t('sheet.testModeDescription')
                        : t('sheet.testModeNoInputsDescription')}
                    </SheetDescription>
                  </>
                ) : (
                  <>
                    <SheetTitle className="text-xl font-medium">
                      {isReadonly
                        ? t('sheet.readonlyTitle')
                        : hasInputSpecs
                          ? t('sheet.title')
                          : t('sheet.triggersTitle')}
                    </SheetTitle>
                    <SheetDescription className="text-base">
                      {t('sheet.description')}
                    </SheetDescription>
                  </>
                )}
              </SheetHeader>

              {/* Scrollable content area */}
              <div className="flex-1 overflow-y-auto px-5 w-full">
                {/* Trigger Type Selection */}
                {!isRunMode && !isTestMode && (
                  <TriggerTypeSelector
                    inputSpecs={inputSpecs}
                    automationId={automationId}
                    automationName={automationName}
                    isReadOnly={isReadonly}
                    isDraft={isDraft}
                    invocationDetails={invocationDetails}
                    defaultInputKeys={defaultInputKeys}
                  />
                )}
                <div
                  data-tour-id="inputs-triggers-panel"
                  onKeyDown={handleInputsKeyDown}
                >
                  {hasInputSpecs ? (
                    <AutomationInputs
                      inputSpecs={inputSpecs}
                      values={values}
                      errors={errors}
                      onValuesChange={onValuesChange}
                      isReadonly={isReadonly}
                      defaultInputKeys={defaultInputKeys}
                      onToggleDefault={onToggleDefault}
                      fileHistoryMap={fileHistoryMap}
                      onSelectHistoryFile={handleSelectHistoryFile}
                    />
                  ) : (
                    <EmptyState
                      title={t('emptyState.title')}
                      className="min-h-96"
                      description={
                        <Flex direction="column" align="center">
                          <Text level="small" color="muted">
                            {t('emptyState.noInputsRequired')}
                          </Text>
                          {/* The trigger hint points at the trigger selector,
                              which test mode never renders — a one-off test has
                              no trigger to configure. */}
                          {!isTestMode && (
                            <Text level="small" color="muted">
                              {t('emptyState.addTriggerHint')}
                            </Text>
                          )}
                        </Flex>
                      }
                    />
                  )}
                </div>
              </div>
            </div>
            {!isReadonly && hasInputSpecs && (
              <SheetFooter className="p-5 border-none">
                <Flex justify="end" align="center" className="gap-2">
                  {hasValues && (
                    <Button variant="outline" onClick={handleClearAll}>
                      {t('actions.clearAll')}
                    </Button>
                  )}
                  {isRunMode ? (
                    <Button onClick={handleRunAction}>
                      {t('sheet.saveAndRun')}
                    </Button>
                  ) : isTestMode ? (
                    <Button
                      data-testid="test-inputs-button"
                      onClick={handleRunAction}
                    >
                      {t('sheet.test')}
                    </Button>
                  ) : (
                    <Button
                      data-testid="save-inputs-button"
                      onClick={onSaveInputs}
                    >
                      {t('sheet.save')}
                    </Button>
                  )}
                </Flex>
              </SheetFooter>
            )}
            {!isReadonly && !hasInputSpecs && (
              <SheetFooter className="p-5 border-none">
                <Flex justify="end" align="center" className="gap-2">
                  <Button variant="outline" onClick={() => onOpenChange(false)}>
                    {tCommon('common.action.close')}
                  </Button>
                  {/* An automation with no inputs still needs a way to launch a
                      test run — without this the sheet is a dead end and the
                      Test button in the page header can never reach `onRun`
                      (ENG-4887). Run has no equivalent gap: it invokes directly
                      and only opens this sheet when validation fails. */}
                  {isTestMode && (
                    <Button
                      data-testid="test-inputs-button"
                      onClick={handleRunAction}
                    >
                      {t('sheet.test')}
                    </Button>
                  )}
                </Flex>
              </SheetFooter>
            )}
          </SheetContent>
        </Sheet>
        {isOpen && isDraft && !isRunMode && !isTestMode && (
          <ManagedTourProvider tourConfig={inputsTriggersTour}>
            <TourTrigger tourConfig={inputsTriggersTour} />
          </ManagedTourProvider>
        )}
      </>
    );
  },
);

AutomationInputsSheet.displayName = 'AutomationInputsSheet';
